// Heart Profile Page
// Page for displaying Heart Games results and insights

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../providers';
import { HeartProfile as HeartProfileDataType, HeartScore } from '../../types/heartGames';
import { GameService } from '../../services/HeartGames/gameService';
import { ScoringService } from '../../services/HeartGames/scoringService';
import { PremiumModal } from '../../components/Premium';
import {
  HeartGamesContainer,
  HeartGamesHeader,
  HeartGamesTitle,
  HeartGamesButton,
  HeartGamesLoading,
  HeartGamesError
} from '../../styles/HeartGames/Shared.styles';
import {
  ResultsContainer,
  HeartProfileHeader,
  HeartProfileType,
  HeartProfileDescription,
  HeartProfileConfidence,
  TraitsGrid,
  TraitCard,
  TraitName,
  TraitValue,
  TraitDescription,
  InsightsSection,
  InsightsTitle,
  InsightsList,
  InsightItem,
  InsightText,
  RecommendationsSection,
  RecommendationsTitle,
  RecommendationsList,
  RecommendationItem,
  RecommendationTitle,
  RecommendationText,
  CompatibilitySection,
  CompatibilityTitle,
  CompatibilityGrid,
  CompatibilityCard,
  CompatibilityType,
  CompatibilityScore,
  CompatibilityDescription,
  ResultsActions
} from '../../styles/HeartGames/Results.styles';

// Each MBTI axis, with the descriptive copy for whichever side is dominant.
const DIMENSIONS = [
  { a: 'E', b: 'I', aName: 'Extraversion', bName: 'Introversion',
    aDesc: 'You gain energy from social interaction', bDesc: 'You recharge through quiet reflection' },
  { a: 'S', b: 'N', aName: 'Sensing', bName: 'Intuition',
    aDesc: 'You focus on concrete details and the present', bDesc: 'You focus on patterns and possibilities' },
  { a: 'T', b: 'F', aName: 'Thinking', bName: 'Feeling',
    aDesc: 'You decide with logic and objectivity', bDesc: 'You decide with values and empathy' },
  { a: 'J', b: 'P', aName: 'Judging', bName: 'Perceiving',
    aDesc: 'You prefer structure and planning', bDesc: 'You prefer flexibility and spontaneity' },
] as const;

// Turn the stored raw MBTI score into the display profile: for each axis pick
// the dominant side and express its strength as a percentage.
function buildProfileFromScore(userId: string, score: HeartScore): HeartProfileDataType {
  const s = score as unknown as Record<string, number>;
  const traits = DIMENSIONS.map((d) => {
    const av = s[d.a] ?? 0;
    const bv = s[d.b] ?? 0;
    const total = av + bv || 1;
    const aWins = av >= bv;
    return {
      name: aWins ? d.aName : d.bName,
      value: Math.round(((aWins ? av : bv) / total) * 100),
      description: aWins ? d.aDesc : d.bDesc,
      examples: [],
    };
  });

  const compatibilityScores = ScoringService.getCompatibilityScores(score.type).map((c) => ({
    type: c.type,
    score: c.score,
    description: c.description,
    strengths: [],
    challenges: [],
  }));

  return {
    userId,
    type: score.type,
    confidence: score.confidence,
    traits,
    insights: ScoringService.generateInsights(score),
    recommendations: ScoringService.generateRecommendations(score),
    compatibility: { compatibleTypes: [], incompatibleTypes: [], compatibilityScores },
    lastUpdated: new Date(),
  };
}

export const HeartProfile: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const [profile, setProfile] = useState<HeartProfileDataType | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'not-completed' | 'error'>('loading');
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);

  const passedScore = (location.state as { score?: HeartScore } | null)?.score;

  // Which game the shown result came from. Needed for the compatibility link,
  // and not always known up front: reached from the profile badge there is no
  // :gameId in the URL, so it comes back with the session.
  const [resultGameId, setResultGameId] = useState<string | undefined>(gameId);

  useEffect(() => {
    // NO GAME ID IS A VALID WAY TO ARRIVE. /heart-games/profile is routed in
    // App.tsx with no :gameId, so this used to fail on arrival every time —
    // the app's own "your Heart Profile" URL always showed an error. Without a
    // game id we simply ask for the latest completed result of any game.

    // Fast path: score handed over from the just-finished game — render at once.
    if (passedScore && passedScore.type && passedScore.type !== 'UNKNOWN') {
      setProfile(buildProfileFromScore(user?.id || '', passedScore));
      setStatus('ready');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // Fallback (refresh / direct navigation / profile badge): the completed
        // session carries the real MBTI score computed when the game finished.
        const session = await GameService.getLatestSession(gameId);
        if (cancelled) return;
        const score = session?.score as HeartScore | undefined;
        if (score && score.type && score.type !== 'UNKNOWN') {
          setResultGameId(session?.gameId ?? gameId);
          setProfile(buildProfileFromScore(user?.id || '', score));
          setStatus('ready');
        } else {
          setStatus('not-completed');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [gameId, user?.id, passedScore]);

  const handleRetakeGames = () => {
    navigate('/heart-games');
  };

  const handleViewCompatibility = () => {
    navigate(`/heart-games/${resultGameId || 'heart-choices-1'}/compatibility`);
  };

  if (status === 'loading') {
    return (
      <HeartGamesContainer>
        <HeartGamesLoading>Loading your Heart Profile...</HeartGamesLoading>
      </HeartGamesContainer>
    );
  }

  if (status === 'error') {
    return (
      <HeartGamesContainer>
        <HeartGamesError>We couldn't load your Heart Profile. Please try again.</HeartGamesError>
      </HeartGamesContainer>
    );
  }

  if (status === 'not-completed' || !profile) {
    return (
      <HeartGamesContainer>
        <HeartGamesHeader>
          <HeartGamesTitle>💝 Your Heart Profile</HeartGamesTitle>
          <HeartGamesButton variant="outline" onClick={() => navigate('/heart-games')}>
            ← Back to Games
          </HeartGamesButton>
        </HeartGamesHeader>
        <div style={{ padding: '2rem', textAlign: 'center', maxWidth: '520px', margin: '0 auto' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💭</div>
          <h2 style={{ fontSize: '1.4rem', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
            No results yet
          </h2>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '1.75rem' }}>
            Finish this game to unlock your personality profile and dating insights.
          </p>
          <HeartGamesButton
            variant="primary"
            onClick={() => navigate(`/heart-games/${gameId || 'heart-choices-1'}/play`)}
            style={{ width: '100%', justifyContent: 'center', padding: '1rem' }}
          >
            Play Now
          </HeartGamesButton>
        </div>
      </HeartGamesContainer>
    );
  }

  const displayProfile = profile;
  const typeInfo = ScoringService.getTypeInfo(displayProfile.type);

  return (
    <HeartGamesContainer>
      <HeartGamesHeader>
        <div>
          <HeartGamesTitle>
            💝 Your Heart Profile
          </HeartGamesTitle>
        </div>
        <HeartGamesButton variant="outline" onClick={() => navigate('/heart-games')}>
          ← Back to Games
        </HeartGamesButton>
      </HeartGamesHeader>

      <ResultsContainer>
        <HeartProfileHeader>
          <HeartProfileType>
            {displayProfile.type} - {typeInfo.name}
          </HeartProfileType>
          <HeartProfileDescription>
            {typeInfo.description}.
          </HeartProfileDescription>
          <HeartProfileConfidence>
            🎯 {displayProfile.confidence}% confidence
          </HeartProfileConfidence>
        </HeartProfileHeader>

        {/* Earned Badge Section */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 140, 66, 0.15) 0%, rgba(255, 140, 66, 0.05) 100%)',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '2rem',
          border: '1px solid rgba(255, 140, 66, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
        }}>
          <div style={{
            fontSize: '4rem',
            filter: 'drop-shadow(0 0 15px rgba(255, 140, 66, 0.6))',
            flexShrink: 0
          }}>
            🏅
          </div>
          <div>
            {/* White, not the dark orange and near-black these were. This card
                sits on the page background, which is dark, so #d35400 and
                #333333 left the announcement almost unreadable — the one thing
                on the page that is meant to feel like a reward. */}
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#ffffff', fontSize: '1.5rem', fontWeight: '800' }}>Heart Choices Badge Earned</h3>
            <p style={{ margin: 0, color: '#ffffff', lineHeight: '1.6', fontSize: '1rem' }}>
              <strong>Your profile is now supercharged!</strong> 🚀 <br />
              This badge signals to our AI that we understand your core values. We're now prioritizing your profile to potential matches who truly align with your personality, placing you in front of the right people to increase your chances of a genuine connection.
            </p>
          </div>
        </div>

        {/* Traits */}
        <TraitsGrid>
          {displayProfile.traits.map((trait, index) => (
            <TraitCard key={index}>
              <TraitName>{trait.name}</TraitName>
              <TraitValue>{trait.value}%</TraitValue>
              <TraitDescription>{trait.description}</TraitDescription>
            </TraitCard>
          ))}
        </TraitsGrid>

        {/* Insights */}
        <InsightsSection>
          <InsightsTitle>Your Personality Insights</InsightsTitle>
          <InsightsList>
            {displayProfile.insights.map((insight, index) => (
              <InsightItem key={index}>
                <InsightText>{insight}</InsightText>
              </InsightItem>
            ))}
          </InsightsList>
        </InsightsSection>

        {/* Recommendations */}
        <RecommendationsSection>
          <RecommendationsTitle>Dating Recommendations</RecommendationsTitle>
          <RecommendationsList>
            {displayProfile.recommendations.map((recommendation, index) => (
              <RecommendationItem key={index}>
                <RecommendationTitle>Recommendation {index + 1}</RecommendationTitle>
                <RecommendationText>{recommendation}</RecommendationText>
              </RecommendationItem>
            ))}
          </RecommendationsList>
        </RecommendationsSection>

        {/* Compatibility */}
        {displayProfile.compatibility.compatibilityScores.length > 0 && (
          <CompatibilitySection>
            <CompatibilityTitle>Compatibility Overview</CompatibilityTitle>
            <CompatibilityGrid>
              {displayProfile.compatibility.compatibilityScores.map((compatibility, index) => (
                <CompatibilityCard key={index}>
                  <CompatibilityType>{compatibility.type}</CompatibilityType>
                  <CompatibilityScore>{compatibility.score}%</CompatibilityScore>
                  <CompatibilityDescription>{compatibility.description}</CompatibilityDescription>
                </CompatibilityCard>
              ))}
            </CompatibilityGrid>
          </CompatibilitySection>
        )}

        {/* Actions */}
        <ResultsActions>
          <HeartGamesButton variant="outline" onClick={handleRetakeGames}>
            Retake Games
          </HeartGamesButton>

          <HeartGamesButton onClick={handleViewCompatibility}>
            View Compatibility
          </HeartGamesButton>
        </ResultsActions>
      </ResultsContainer>

      {/* Premium Modal */}
      <PremiumModal
        isOpen={isPremiumModalOpen}
        onClose={() => setIsPremiumModalOpen(false)}
      />
    </HeartGamesContainer>
  );
};
